/** Dispatched by any Schedule a Tour CTA; Header listens and opens the embed popup. */
export const OPEN_SCHEDULE_TOUR_MODAL_EVENT = 'vibesite:open-schedule-tour-modal';

/** Legacy key from an earlier auto-open-on-redirect flow; cleared on boot so it never reopens the popup. */
const LEGACY_SCHEDULE_TOUR_MODAL_PENDING_KEY = 'vibesite:open-schedule-tour-modal';

export function requestOpenScheduleTourModal(e?: { preventDefault?: () => void }) {
  e?.preventDefault?.();
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_SCHEDULE_TOUR_MODAL_EVENT));
}

/**
 * In-page href for tour CTAs. A hash never leaves the current page, so a click
 * before hydration (or inside the editor preview) can't land on a tour page.
 */
export const SCHEDULE_TOUR_HASH = '#schedule-a-tour';

/** True when an href targets /schedule-a-tour, /tour, or the in-page tour hash. */
export function isScheduleTourHref(href: string | null | undefined): boolean {
  if (!href) return false;
  const trimmed = href.trim();
  if (trimmed === SCHEDULE_TOUR_HASH) return true;
  try {
    const path = /^https?:\/\//i.test(trimmed)
      ? new URL(trimmed).pathname
      : trimmed.split(/[?#]/)[0];
    const normalized = ('/' + path.replace(/^\/+/, '')).replace(/\/+$/, '') || '/';
    return (
      normalized === '/tour' ||
      normalized === '/schedule-a-tour' ||
      normalized.endsWith('/schedule-a-tour')
    );
  } catch {
    return false;
  }
}

/** True for the /schedule-a-tour and /tour URLs, which must never render as pages. */
export function isScheduleTourLandingPath(path: string | null | undefined): boolean {
  return !!path && !path.startsWith('#') && isScheduleTourHref(path);
}

/**
 * Anchor props for an editable CTA. Tour URLs become a popup trigger on the
 * current page; every other URL passes through unchanged.
 */
export function ctaLinkProps(url: string, target?: string | null) {
  if (isScheduleTourHref(url)) {
    return {
      href: SCHEDULE_TOUR_HASH,
      target: '_self',
      onClick: requestOpenScheduleTourModal,
      'data-vibesite-event': 'schedule_tour_clicked',
    };
  }
  return { href: url, target: target || '_self' };
}

/** Drop any leftover session flag so the popup never auto-opens on load. */
export function clearLegacyScheduleTourModalPending() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(LEGACY_SCHEDULE_TOUR_MODAL_PENDING_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}
